/**
 * Copyright 2023 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { Provider } from "~/generated/graphql";
import { useEstateRecords } from "~/hooks";
import { isCloudResource, noop } from "~/tools";
import { isEmpty } from "~/tools/size";
import { ProviderSpecificDetails } from "../types";

export function useEstateNidsByProviderDetails(
  provider: Provider,
  details: ProviderSpecificDetails[]
): { [providerId: string]: string } {
  const providerIDs = [...new Set(details.map(({ providerId }) => providerId))];

  const { data: searchResult } = useEstateRecords(
    {
      variables: {
        limit: providerIDs.length,
        filter: {
          provider: [provider],
          providerId: providerIDs,
          typeSubtype: [
            ...new Set(details.map(({ typeSubtype }) => typeSubtype)),
          ],
        },
      },
      // Prevent sending query with `limit: 0`, it will cause Bad Request error
      skip: isEmpty(providerIDs),
    },
    noop
  );

  const records = searchResult?.resources ?? [];
  return Object.fromEntries(
    records
      .filter(isCloudResource)
      .filter((r) => providerIDs.includes(r.name))
      .map((r) => [r.name, r.id])
  );
}
